import { useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { MasterSyncCard } from '../components/MasterSync.tsx';
import { SYSTEM_ATTRS } from '@berytus/contracts';
import { ProductEditor, type EditorTab } from './ProductEditor.tsx';
import { kitTitle } from '@berytus/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateKitBody, PharmaFields, ProductDetail } from '@berytus/contracts';
import { api, brl, dateTime, int, pct } from '../api.ts';
import { ErrorBox, GTIN, Loading, MatchTag, SOURCE, Tag } from '../components/ui.tsx';
import { titleOf } from './Catalog.tsx';
import { useMe } from '../components/AuthGate.tsx';
import { ContentSyncDialog } from '../components/ContentSync.tsx';
import { RefreshCw } from 'lucide-react';
import { can } from '@berytus/contracts';

const FLAG_LABEL: Record<string, string> = {
  controlled: 'Controlado',
  thermolabile: 'Termolábil',
  antibiotic: 'Antibiótico',
  blocked: 'Bloqueado',
};

const TABS: [string, string][] = [['resumo', 'Resumo'], ['cadastro', 'Cadastro'], ['fiscal', 'Fiscal'], ['logistica', 'Logística'], ['fotos', 'Fotos'], ['atributos', 'Atributos']];

export function ProductPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([t]) => t === params.get('aba')) ? params.get('aba')! : 'resumo';
  const q = useQuery({ queryKey: ['product', id], queryFn: () => api<ProductDetail>(`/catalog/products/${id}`) });
  const canEdit = can(useMe().tenant.access, 'catalog', 'write');

  return (
    <>
      <div className="crumbs"><Link to="/catalogo">Catálogo</Link><span aria-hidden>/</span><span>{q.data?.sku ?? '…'}</span></div>
      {q.isPending ? <Loading /> : q.error ? <ErrorBox error={q.error} /> : (
        <div className="stack-y">
          <Head p={q.data} />
          <div className="seg" role="tablist" aria-label="Seções do produto">
            {TABS.map(([t, l]) => (
              <button key={t} type="button" role="tab" aria-pressed={tab === t} onClick={() => setParams(t === 'resumo' ? {} : { aba: t }, { replace: true })}>{l}</button>
            ))}
          </div>
          {tab === 'resumo' ? <><Detail p={q.data} /><SystemAttributes p={q.data} /><MasterSyncCard productId={q.data.id} canEdit={canEdit} /></> : <ProductEditor productId={q.data.id} tab={tab as EditorTab} />}
        </div>
      )}
    </>
  );
}

function Head({ p }: { p: ProductDetail }) {
  const [syncing, setSyncing] = useState(false);
  const admin = can(useMe().tenant.access, 'catalog', 'write');
  const image = p.cover ?? p.ficha?.images[0];
  const flags = Object.entries(p.flags).filter(([, v]) => v).map(([k]) => FLAG_LABEL[k] ?? k);
  return (
    <div className="detail-head" style={{ marginBottom: 0 }}>
      {image ? <img className="hero" src={image} alt="" /> : <div className="hero blank" aria-hidden />}
      <div style={{ minWidth: 0, flex: 1 }}>
        <h1 className={p.title ? undefined : 'untitled'}>{titleOf(p)}</h1>
        <p className="muted" style={{ margin: '0 0 8px' }}>
          SKU {p.sku}{p.matchedGtin && <> · EAN {p.matchedGtin}</>}{p.category && <> · {p.category}</>}
        </p>
        <div className="tags">
          <MatchTag status={p.matchStatus} />
          {!p.active && <Tag tone="off">Inativo</Tag>}
          {flags.map((f) => <Tag key={f} tone="warn">{f}</Tag>)}
        </div>
        {p.base && (
          <p className="small" style={{ margin: '8px 0 0' }}>
            Kit de {p.kitUnits} unidades de <Link to={`/catalogo/${p.base.id}`}>{titleOf(p.base)}</Link>
          </p>
        )}
      </div>
      {admin && (
        <div>
          <button className="btn" type="button" title="Envia fotos, descrição e título do cadastro para os anúncios deste produto em todos os canais" onClick={() => setSyncing(true)}>
            <RefreshCw size={16} /> Sincronizar anúncios
          </button>
        </div>
      )}
      {syncing && <ContentSyncDialog selection={{ productIds: [p.id], label: 'Este produto' }} onClose={() => setSyncing(false)} />}
    </div>
  );
}

function Detail({ p }: { p: ProductDetail }) {
  const pharmacy = useMe().tenant.vertical === 'pharmacy';

  return (
    <div className="stack-y">
      <section className="panel">
        <h2>Dados comerciais</h2>
        <dl className="facts">
          <div><dt>Custo</dt><dd>{brl(p.cost)}</dd></div>
          <div><dt>Custo sem impostos</dt><dd>{brl(p.costNet)}</dd></div>
          {(p.pmc || pharmacy) && <div><dt>PMC</dt><dd>{brl(p.pmc)}</dd></div>}
          <div><dt>Margem mínima</dt><dd>{pct(p.minMargin)}</dd></div>
          <div><dt>Preço fixo</dt><dd>{brl(p.fixedPrice)}</dd></div>
          <div><dt>Curva</dt><dd>{p.curve ?? '—'}</dd></div>
          {p.isKit && <div><dt>Desconto do kit</dt><dd>{p.kitDiscountPct ? `${Number(p.kitDiscountPct).toLocaleString('pt-BR')}%` : '—'}</dd></div>}
          <div><dt>Atualizado na origem</dt><dd>{dateTime(p.sourceUpdatedAt)}</dd></div>
        </dl>
      </section>

      <div className="cols">
        <section className="panel">
          <h2>Estoque</h2>
          {p.stock.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>Sem estoque informado.</p>
          ) : (
            <table className="simple">
              <thead><tr><th>Loja</th><th className="num">Em estoque</th><th className="num">Segurança</th><th className="num">Disponível</th></tr></thead>
              <tbody>
                {p.stock.map((s) => (
                  <tr key={s.storeCode}>
                    <td>{s.storeName}<div className="code">atualizado {dateTime(s.updatedAt)}</div></td>
                    <td className="num">{int(s.quantity)}</td>
                    <td className="num">{int(s.safetyStock)}</td>
                    <td className="num"><b>{int(s.available)}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {p.isKit && <p className="small muted" style={{ marginBottom: 0 }}>O estoque do kit é calculado: disponível do produto base ÷ {p.kitUnits}.</p>}
        </section>

        <section className="panel">
          <h2>Códigos de barras recebidos</h2>
          {p.gtin.status === 'corrected' && (
            <p className="small muted" style={{ marginTop: 0 }}>EAN corrigido: zeros à esquerda (recebido {p.gtin.received}). Vale {p.gtin.ean}.</p>
          )}
          {p.gtin.status === 'invalid' && (
            <p className="small" style={{ marginTop: 0, color: 'var(--err)' }}>GTIN inválido: nenhum código fecha o dígito verificador. O produto não vai para os canais até o EAN ser corrigido no cadastro.</p>
          )}
          {p.gtins.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>Nenhum EAN informado para este produto.</p>
          ) : (
            <table className="simple">
              <thead><tr><th>Campo</th><th>Valor</th><th>Situação</th></tr></thead>
              <tbody>
                {p.gtins.map((g, i) => (
                  <tr key={i}>
                    <td className="small">{SOURCE[g.source] ?? g.source}</td>
                    <td className="code">{g.gtin ?? g.rawValue}</td>
                    <td>
                      <Tag tone={GTIN[g.status].tone}>{GTIN[g.status].label}</Tag>
                      {g.usedForMatch && <> <Tag tone="info">Usado no vínculo</Tag></>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      <section className="panel">
        <h2>Ficha no catálogo mestre</h2>
        {!p.ficha ? (
          <p className="muted" style={{ margin: 0 }}>
            {p.matchStatus === 'ambiguous'
              ? 'Os EANs deste produto apontam para fichas diferentes. Ele precisa de revisão antes de ser vinculado.'
              : p.matchStatus === 'no_gtin'
                ? 'Este produto não tem EAN válido. Ele pode ser anunciado assim mesmo, mas sem ficha de catálogo nos canais.'
                : 'Nenhum EAN válido deste produto foi encontrado no catálogo mestre.'}
          </p>
        ) : (
          <>
            <dl className="facts">
              <div><dt>Nome na ficha</dt><dd>{p.ficha.title}</dd></div>
              {(p.ficha.brand || p.ficha.manufacturer) && (
                <div><dt>Marca / fabricante</dt><dd>{[p.ficha.brand, p.ficha.manufacturer].filter(Boolean).join(' · ')}</dd></div>
              )}
              {p.ficha.pharma && <PharmaFacts f={p.ficha.pharma} />}
              <div><dt>EANs da ficha</dt><dd className="code">{p.ficha.gtins.join(', ')}</dd></div>
            </dl>
            {p.ficha.images.length > 0 && (
              <div className="gallery">{p.ficha.images.map((u) => <img key={u} src={u} alt="" loading="lazy" />)}</div>
            )}
          </>
        )}
      </section>

      {!p.isKit && <Kits p={p} />}
    </div>
  );
}

/** Campos que só existem em fichas de farmácia. */
function PharmaFacts({ f }: { f: PharmaFields }) {
  return (
    <>
      <div><dt>Princípio ativo</dt><dd>{f.activeIngredient ?? '—'}</dd></div>
      <div><dt>Dosagem</dt><dd>{f.dosage ?? '—'}</dd></div>
      <div><dt>Apresentação</dt><dd>{f.presentation ?? '—'}</dd></div>
      <div><dt>Registro Anvisa</dt><dd>{f.anvisaCode ?? '—'}</dd></div>
      <div><dt>ATC</dt><dd>{f.atcCode ?? '—'}</dd></div>
      <div><dt>Tipo</dt><dd>{f.isMedicine ? (f.isOtc ? 'Medicamento isento de prescrição' : 'Medicamento') : 'Não medicamento'}</dd></div>
    </>
  );
}

function Kits({ p }: { p: ProductDetail }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const create = useMutation({
    // Cria o kit e já monta a foto dele (a do produto repetida N vezes). Sem foto possível, o kit fica criado e avisa.
    mutationFn: async (body: CreateKitBody) => {
      const kit = await api<{ id: string; sku: string }>(`/catalog/products/${p.id}/kits`, { method: 'POST', json: body });
      let photo: string | null = null;
      try {
        await api(`/catalog/products/${kit.id}/kit-image`, { method: 'POST' });
      } catch (err) {
        photo = err instanceof Error ? err.message : 'não foi possível gerar a foto';
      }
      return { kit, photo };
    },
    onSuccess: ({ kit, photo }) => {
      setDone(photo ? `Kit ${kit.sku} criado, sem foto: ${photo} Gere depois no kit.` : `Kit ${kit.sku} criado com a foto.`);
      qc.invalidateQueries({ queryKey: ['product', kit.id] });
      setError(null);
      qc.invalidateQueries({ queryKey: ['product', p.id] });
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['summary'] });
    },
    onError: (err) => { setDone(null); setError(err instanceof Error ? err.message : 'Não foi possível criar o kit.'); },
  });

  const [units, setUnits] = useState(2);
  const [title, setTitle] = useState('');
  const suggestion = kitTitle(p.title ?? p.sku, Math.max(2, units || 2));
  const effective = title.trim() || suggestion;
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const discount = String(f.get('discount') ?? '').replace(',', '.');
    create.mutate({ units, ...(discount ? { discountPct: Number(discount) } : {}), title: effective });
  };

  return (
    <section className="panel">
      <h2>Kits deste produto</h2>
      <p className="small muted" style={{ marginTop: -6 }}>
        O kit acompanha este produto: estoque = disponível ÷ unidades; custo e PMC = os deste × unidades; preço em cada canal = preço deste × unidades − desconto do kit. Mudou aqui, muda no kit.
      </p>
      {p.kits.length > 0 && (
        <table className="simple">
          <thead><tr><th>SKU</th><th>Nome</th><th className="num">Unidades</th><th className="num">Desconto</th><th className="num">Disponível</th><th className="num">Custo</th><th className="num">PMC</th><th className="num">Preço ML</th></tr></thead>
          <tbody>
            {p.kits.map((k) => (
              <tr key={k.id}>
                <td className="code"><Link to={`/catalogo/${k.id}`}>{k.sku}</Link></td>
                <td>{k.title}</td>
                <td className="num">{k.units}</td>
                <td className="num">{k.discountPct ? `${Number(k.discountPct).toLocaleString('pt-BR')}%` : '—'}</td>
                <td className="num">{int(k.available)}</td>
                <td className="num">{brl(k.cost)}</td>
                <td className="num">{brl(k.pmc)}</td>
                <td className="num">{brl(k.meliPrice)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form className="kit-form" onSubmit={submit}>
        <div className="field">
          <label htmlFor="units">Unidades</label>
          <input className="input" id="units" name="units" type="number" min={2} max={100} value={units} required onChange={(e) => setUnits(Number(e.target.value))} />
        </div>
        <div className="field">
          <label htmlFor="discount">Desconto (%)</label>
          <input className="input" id="discount" name="discount" type="text" inputMode="decimal" placeholder="opcional" />
        </div>
        <div className="field" style={{ flex: '2 1 360px' }}>
          <label htmlFor="title">Nome do kit</label>
          <input className="input" id="title" name="title" type="text" maxLength={250} value={title} placeholder={suggestion}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              // Tab aceita a sugestão (campo vazio ou começo dela digitado).
              if (e.key === 'Tab' && !e.shiftKey && title !== suggestion && (!title || suggestion.toLowerCase().startsWith(title.toLowerCase()))) {
                e.preventDefault();
                setTitle(suggestion);
              }
            }} />
          <span className="small muted">
            {effective.length}/60 caracteres{effective.length > 60 && <span style={{ color: 'var(--warn)' }}> · o Mercado Livre aceita até 60</span>}
            {title !== suggestion && <> · <kbd>Tab</kbd> aceita a sugestão</>}
          </span>
        </div>
        <button className="btn primary" type="submit" disabled={create.isPending}>{create.isPending ? 'Criando kit e foto…' : 'Criar kit'}</button>
      </form>
      {error && <div className="error" role="alert" style={{ marginTop: 10 }}>{error}</div>}
      {done && !error && <div className="notice small" role="status" style={{ marginTop: 10 }}>{done}</div>}
    </section>
  );
}

/** Atributos padrão do sistema: somente leitura, alimentados pelo catálogo mestre (seus atributos ficam em Atributos). */
function SystemAttributes({ p }: { p: ProductDetail }) {
  const a = p.systemAttributes;
  return (
    <section className="panel">
      <div className="panel-head"><h2>Atributos do sistema</h2><span className="small muted">somente leitura · vêm do catálogo mestre</span></div>
      {!a ? <div className="small muted">Sem ficha conferida no catálogo mestre: os atributos do sistema aparecem quando o item for vinculado.</div> : (
        <div style={{ display: 'grid', gap: 6, gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
          {SYSTEM_ATTRS.map((d) => {
            const v = a[d.key];
            return <div key={d.key} className="small"><span className="muted">{d.label}: </span><b>{v == null ? '—' : typeof v === 'boolean' ? (v ? 'Sim' : 'Não') : v}</b></div>;
          })}
        </div>
      )}
    </section>
  );
}
